import lodashCloneDeep from "lodash/cloneDeep";
import lodashOrderBy from "lodash/orderBy";

import { httpGet } from "@/services/api.service";
import { encodeAllURLParameters } from "@/common/api.utilities";
import { propertyByLanguage } from "@/common/basic.utilities";
import {
  ENDPOINT_CALENDAR_EVENTS,
  ENDPOINT_CALENDAR_EVENTS_CONFIG,
} from "@/common/constants/api";
import { getCountryLookup, getEventCountries } from "@/common/maps/utilities";
import { getFormattedDate } from "@/common/date.utilities";
import {
  getTagsConfig,
  getTagsLabelsByCodes,
} from "@/services/search/tags.service";

export { _getCalendarEvents as getCalendarEvents };
export { _getCalendarEventsConfig as getCalendarEventsConfig };
export { _getCalendarEventsCategories as getCalendarEventsCategories };
export { _getCalendarEventsSubCategories as getCalendarEventsSubCategories };
export { _getPlannedEventsSubCategories as getPlannedEventsSubCategories };
export { _getCalendarEventsImpactTypes as getCalendarEventsImpactTypes };

let calendarEventsConfig;

const _getCalendarEvents = async (params) => {
  try {
    const url = encodeAllURLParameters({
      baseEndpoint: ENDPOINT_CALENDAR_EVENTS,
      params: params,
    });
    const result = await httpGet({ url });
    const { data } = JSON.parse(result);

    await _formatFeatures(data.features);

    return data;
  } catch (error) {
    console.error(
      "Issue getting calendar events in calendarEvents.service.js:",
      error
    );
    return null;
  }
};

const _formatFeatures = async (features) => {
  const countryLookup = await getCountryLookup();
  const tagsConfig = await getTagsConfig();
  for (const feature of features) {
    _formatFeature(feature, countryLookup, tagsConfig);
  }
};

const _formatFeature = (feature, countryLookup, tagsConfig) => {
  if (!feature.properties) {
    return;
  }

  const language = feature.properties.language ?? "en";

  feature.properties.formatted = {
    language: language,
    occurred: feature.properties?.["@timestamp"] ?? null,
    completed: feature.properties?.["@completed"] ?? null,
    eventDaterange: feature.properties?.["@eventDaterange"] ?? null,
    eventTimelineStartDate:
      feature.properties?.["@eventDaterange"]?.gte ??
      feature.properties?.["@timestamp"] ??
      null,
    eventTimelineEndDate:
      feature.properties?.["@eventDaterange"]?.lte ??
      feature.properties?.["@timestamp"] ??
      null,
    title:
      propertyByLanguage(feature.properties.title, language) ??
      "Oops, can't find a title",
    content: propertyByLanguage(feature.properties.content, language) ?? "",
    countries: getEventCountries(
      feature.properties.location?.metadata?.countryCodes,
      countryLookup
    ),
    relatedCountries: getRelatedCountries(
      feature.properties.location?.metadata?.countryCodes,
      feature.properties.location?.metadata?.relatedCountryCodes,
      countryLookup
    ),
    category: feature.properties.event?.category ?? "",
    dateType: feature.properties.event?.eventDateType ?? null,
    impactCategories: feature.properties.event?.impactCategory ?? "",
    severity: feature.properties.event?.severity ?? "",
    subCategory: feature.properties.event?.subcategory ?? "",
    perpetratorDetails: feature.properties.event?.perpetratorDetails ?? null,
    perpetrator: feature.properties.event?.perpetrator ?? null,
    locationPrecision: feature.properties.event?.locationPrecision ?? null,
    tags: feature.properties.event?.tags
      ? getTagsLabelsByCodes(feature.properties.event.tags, tagsConfig)
      : null,
    source: feature.properties.source ?? null,
  };

  if (
    feature.properties?.["@eventDaterange"] &&
    feature.properties?.["@eventDaterange"]?.gte &&
    feature.properties?.["@eventDaterange"]?.lte
  ) {
    if (
      feature.properties["@eventDaterange"].gte ===
      feature.properties["@eventDaterange"].lte
    ) {
      feature.properties.formatted.dateRange = getFormattedDate(
        feature.properties["@eventDaterange"].gte
      );
    } else {
      feature.properties.formatted.dateRange = `
      ${getFormattedDate(feature.properties["@eventDaterange"].gte)} to 
      ${getFormattedDate(feature.properties["@eventDaterange"].lte)}`;
    }
  }
};

const getRelatedCountries = (
  countryCodes,
  relatedCountryCodes,
  countryLookup
) => {
  return (
    relatedCountryCodes
      ?.filter((cc) => !countryCodes?.includes(cc))
      ?.map((code) => countryLookup[code])
      .filter(Boolean) ?? []
  );
};

const _getCalendarEventsConfig = async () => {
  try {
    const url = ENDPOINT_CALENDAR_EVENTS_CONFIG;
    const result = await httpGet({ url });
    const { data } = JSON.parse(result);
    calendarEventsConfig = data;
    return data;
  } catch (error) {
    console.error(
      "Issue getting calendar events config in calendarEvents.service.js:",
      error
    );
    return null;
  }
};

const _getCalendarEventsTypes = async () => {
  if (!calendarEventsConfig) {
    try {
      const calendarEvents = await _getCalendarEventsConfig();
      if (!calendarEvents) {
        throw "Error getting Calendar Events config";
      }
      return lodashCloneDeep(calendarEventsConfig);
    } catch (error) {
      console.error("Calendar Events API: " + error);
      return null;
    }
  }
  return lodashCloneDeep(calendarEventsConfig);
};

const _getCalendarEventsByType = async (type) => {
  const calendarEvents = await _getCalendarEventsTypes();
  if (calendarEvents && type && calendarEvents[type]) {
    return lodashOrderBy(calendarEvents[type], ["text"]);
  }
  console.error(`Calendar Events API: unable to get ${type}`);
  return null;
};

const _getCalendarEventsCategories = async () => {
  return _getCalendarEventsByType("categories");
};

const _getCalendarEventsSubCategories = async () => {
  return _getCalendarEventsByType("subCategories");
};

const _getPlannedEventsSubCategories = async () => {
  const plannedEvents = [];
  const subCategories = await _getCalendarEventsSubCategories();
  if (subCategories) {
    subCategories.forEach((cat) => {
      if (cat.isPlanned) {
        plannedEvents.push(cat);
      }
    });
  }
  return plannedEvents;
};

const _getCalendarEventsImpactTypes = async () => {
  return _getCalendarEventsByType("impactTypes");
};
